import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { title: "Ståltag", path: "/staaltag", badge: "SPECIALER" },
    { title: "Metrotile og Decra tag", path: "/ydelser/metrotile-og-decra-tag" },
    { title: "Tagrenovering", path: "/ydelser/tagrenovering" },
    { title: "Døre og vinduer", path: "/ydelser/doere-og-vinduer" },
    { title: "Tilbygning og ombygning", path: "/ydelser/tilbygning-og-ombygning" },
    { title: "Totalrenovering", path: "/ydelser/totalrenovering" },
  ];

  return (
    <header className="sticky top-0 z-40 w-full bg-white shadow-sm border-b border-slate-100 transition-all duration-300 relative">
      {/* Top Floating Contact Bar (Attached to Sticky Header - remains pinned when page scrolls) */}
      <div className="absolute top-full inset-x-0 pointer-events-none max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 hidden lg:flex justify-end">
        <div className="pointer-events-auto inline-flex rounded-b-md shadow-md overflow-hidden text-xs">
          <Link 
            to="/kontakt" 
            className="bg-[#d92323] hover:bg-[#b81b1b] text-white font-extrabold uppercase px-6 py-2.5 flex items-center gap-1.5 transition-colors"
          >
            <span>Få et uforpligtende tilbud</span>
            <span className="text-sm leading-none">&raquo;</span>
          </Link>
          <div className="bg-[#11161a] text-white font-bold uppercase tracking-wider px-6 py-2.5 flex items-center gap-6">
            <a href="tel:28594349" className="hover:text-[#d92323] transition-colors whitespace-nowrap flex items-center gap-2">
              <i className="fa-solid fa-phone text-[#d92323]" />
              (+45) 28 59 43 49
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation header */}
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img 
            src="https://assets.auxodev.dk/assets/4aae8b48-1e4d-42c2-9336-5940e0b091a6/logo.png" 
            alt="T.H Byg logo" 
            className="h-10 sm:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop Navigation (min-width: 1200px) */}
        <div className="hidden min-[1200px]:flex items-center gap-8">
          <nav className="flex items-center gap-6 text-xs font-black uppercase tracking-wider text-slate-900">
            <NavLink 
              to="/" 
              end
              className={({ isActive }) => 
                `transition-colors py-1 ${
                  isActive ? 'text-[#d92323] border-b-2 border-[#d92323] font-black' : 'hover:text-[#d92323]'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Ståltag - Main Focus Highlighted */}
            <NavLink 
              to="/staaltag" 
              className={({ isActive }) => 
                `relative transition-colors py-1 flex items-center gap-1.5 ${
                  isActive ? 'text-[#d92323] border-b-2 border-[#d92323] font-black' : 'hover:text-[#d92323]'
                }`
              }
            >
              <span>Ståltag</span>
            </NavLink>

            {/* Ydelser Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setServicesDropdownOpen(true)}
              onMouseLeave={() => setServicesDropdownOpen(false)}
            >
              <NavLink 
                to="/ydelser"
                className={({ isActive }) => 
                  `transition-colors py-1 flex items-center gap-1 ${
                    isActive ? 'text-[#d92323] border-b-2 border-[#d92323] font-black' : 'hover:text-[#d92323]'
                  }`
                }
              >
                <span>Ydelser</span>
                <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
              </NavLink>

              {/* Dropdown Menu */}
              {servicesDropdownOpen && (
                <div className="absolute left-0 top-full pt-2 w-64 z-50">
                  <div className="rounded-md bg-white border border-slate-200 shadow-xl p-2 text-xs font-extrabold uppercase tracking-wider text-slate-900">
                    {services.map((s) => (
                      <NavLink
                        key={s.path}
                        to={s.path}
                        className={({ isActive }) => 
                          `flex items-center justify-between px-3 py-2.5 rounded transition-all ${
                            isActive 
                              ? 'bg-[#d92323] text-white font-black' 
                              : 'hover:bg-slate-100 hover:text-[#d92323]'
                          }`
                        }
                      >
                        <span className="flex items-center gap-2">
                          <i className="fa-solid fa-angle-right text-xs text-[#d92323]" />
                          {s.title}
                        </span>
                        {s.badge && (
                          <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-[#d92323] text-white">
                            {s.badge}
                          </span>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink 
              to="/projekter" 
              className={({ isActive }) => 
                `transition-colors py-1 ${
                  isActive ? 'text-[#d92323] border-b-2 border-[#d92323] font-black' : 'hover:text-[#d92323]'
                }`
              }
            >
              Projekter
            </NavLink>

            <NavLink 
              to="/om-os" 
              className={({ isActive }) => 
                `transition-colors py-1 ${
                  isActive ? 'text-[#d92323] border-b-2 border-[#d92323] font-black' : 'hover:text-[#d92323]'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink 
              to="/kontakt" 
              className={({ isActive }) => 
                `transition-colors py-1 ${
                  isActive ? 'text-[#d92323] border-b-2 border-[#d92323] font-black' : 'hover:text-[#d92323]'
                }`
              }
            >
              Kontakt
            </NavLink>
          </nav>
        </div>

        {/* Mobile / Tablet Burger Button (Screen < 1200px) */}
        <div className="flex min-[1200px]:hidden items-center gap-3">
          <a
            href="tel:28594349"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#d92323] text-white shadow"
            aria-label="Ring op"
          >
            <i className="fa-solid fa-phone text-xs" />
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#11161a] text-white hover:bg-[#d92323] transition-colors"
            aria-label="Åbn menu"
          >
            <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark text-xl' : 'fa-bars text-lg'}`} />
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Drawer (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-200 bg-white px-4 pt-4 pb-6 shadow-2xl max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="flex flex-col gap-1 text-sm font-extrabold uppercase text-slate-900">
            <NavLink 
              to="/" 
              end
              className={({ isActive }) => 
                `px-4 py-3 rounded-lg transition-colors ${
                  isActive ? 'bg-[#d92323] text-white font-black' : 'hover:bg-slate-100'
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Ståltag prominent link */}
            <NavLink 
              to="/staaltag" 
              className={({ isActive }) => 
                `flex items-center justify-between px-4 py-3 rounded-lg transition-colors bg-[#d92323] text-white font-black`
              }
            >
              <span className="flex items-center gap-2">
                <i className="fa-solid fa-house-chimney-crack" />
                Ståltag (Specialist)
              </span>
              <span className="text-[10px] bg-white text-[#d92323] px-2 py-0.5 rounded font-black uppercase">
                Fokus
              </span>
            </NavLink>

            {/* Mobile Services Accordion */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-slate-900 hover:bg-slate-100 transition-colors uppercase font-extrabold"
              >
                <span className="uppercase">Ydelser</span>
                <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {mobileServicesOpen && (
                <div className="ml-4 pl-3 border-l-2 border-[#d92323] my-1 flex flex-col gap-1">
                  {services.map((s) => (
                    <NavLink
                      key={s.path}
                      to={s.path}
                      className={({ isActive }) => 
                        `px-3 py-2 rounded-md text-xs transition-colors ${
                          isActive ? 'text-[#d92323] font-black bg-slate-100' : 'text-slate-700 hover:text-[#d92323]'
                        }`
                      }
                    >
                      {s.title}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink 
              to="/projekter" 
              className={({ isActive }) => 
                `px-4 py-3 rounded-lg transition-colors ${
                  isActive ? 'bg-[#d92323] text-white font-black' : 'hover:bg-slate-100'
                }`
              }
            >
              Projekter
            </NavLink>

            <NavLink 
              to="/om-os" 
              className={({ isActive }) => 
                `px-4 py-3 rounded-lg transition-colors ${
                  isActive ? 'bg-[#d92323] text-white font-black' : 'hover:bg-slate-100'
                }`
              }
            >
              Om os
            </NavLink>

            <NavLink 
              to="/kontakt" 
              className={({ isActive }) => 
                `px-4 py-3 rounded-lg transition-colors ${
                  isActive ? 'bg-[#d92323] text-white font-black' : 'hover:bg-slate-100'
                }`
              }
            >
              Kontakt
            </NavLink>

            <div className="pt-4 border-t border-slate-200 mt-2 flex flex-col gap-3">
              <Link
                to="/kontakt"
                className="w-full text-center rounded-full bg-[#d92323] py-3 text-xs font-black uppercase tracking-wider text-white shadow-lg"
              >
                Få et uforpligtende tilbud &raquo;
              </Link>

              <div className="flex flex-col gap-2 pt-2 text-xs text-slate-600">
                <a href="tel:28594349" className="flex items-center gap-2 text-slate-900 font-bold whitespace-nowrap">
                  <i className="fa-solid fa-phone text-[#d92323]" />
                  <span className="whitespace-nowrap">(+45) 28 59 43 49</span>
                </a>
                <a href="mailto:jacob.t.h.byg@gmail.com" className="flex items-center gap-2 whitespace-nowrap">
                  <i className="fa-solid fa-envelope text-[#d92323]" />
                  <span className="whitespace-nowrap">jacob.t.h.byg@gmail.com</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
